import { Timestamp } from 'firebase/firestore';

// Re-export Timestamp for use in other files
export { Timestamp };

/**
 * Creates a Firestore Timestamp from a Date object or current time.
 * @param {Date|number|string} [date] - Date to convert, defaults to current time
 * @returns {Timestamp} Firestore Timestamp object
 */
export const createTimestamp = (date = new Date()) => {
  if (date instanceof Date) {
    return Timestamp.fromDate(date);
  }
  if (typeof date === 'number') {
    return Timestamp.fromMillis(date);
  }
  if (typeof date === 'string') {
    return Timestamp.fromDate(new Date(date));
  }
  return Timestamp.now();
};

/**
 * Gets the current timestamp.
 * @returns {Timestamp} Current Firestore Timestamp
 */
export const getCurrentTimestamp = () => Timestamp.now();

/**
 * Recursively converts Firestore Timestamps to ISO strings.
 * @param {any} data The data to process.
 * @returns {any} The processed data with Timestamps converted.
 */
export const convertTimestampsToISO = (data) => {
  if (data instanceof Timestamp) {
    return data.toDate().toISOString();
  }
  if (Array.isArray(data)) {
    return data.map(convertTimestampsToISO);
  }
  if (data !== null && typeof data === 'object') {
    return Object.keys(data).reduce((acc, key) => {
      acc[key] = convertTimestampsToISO(data[key]);
      return acc;
    }, {});
  }
  return data;
};

/**
 * Flattens departments from a facility document.
 * @param {object} facility - The facility document data
 * @returns {Array} Flat array of departments
 */
export function flattenDepartments(facility) {
  return (facility.departments || []).map(dept => ({
    ...dept,
    id: dept.id || dept.code || dept.name,
    code: dept.code,
    name: dept.name,
    status: dept.status || 'ACTIVE',
  }));
}

/**
 * Flattens rooms from a facility document.
 * @param {object} facility - The facility document data
 * @returns {Array} Flat array of rooms, each with department reference
 */
export function flattenRooms(facility) {
  const rooms = [];
  (facility.departments || []).forEach(dept => {
    (dept.rooms || []).forEach(room => {
      rooms.push({
        ...room,
        roomId: room.code || room.id || room.name,
        code: room.code,
        name: room.name,
        type: room.type || 'Room',
        status: room.status || 'ACTIVE',
        departmentId: dept.id || dept.code || dept.name,
        departmentName: dept.name,
      });
    });
  });
  return rooms;
}

/**
 * Flattens beds from a facility document.
 * @param {object} facility - The facility document data
 * @returns {Array} Flat array of beds, each with room and department reference
 */
export function flattenBeds(facility) {
  const beds = [];
  (facility.departments || []).forEach(dept => {
    (dept.rooms || []).forEach(room => {
      (room.beds || []).forEach(bed => {
        beds.push({
          ...bed,
          bedId: bed.code || bed.bedId || bed.name,
          code: bed.code,
          name: bed.name,
          status: bed.status || 'available',
          roomId: room.code || room.id || room.name,
          roomName: room.name,
          departmentId: dept.id || dept.code || dept.name,
          departmentName: dept.name,
        });
      });
    });
  });
  return beds;
}
